import { Controller } from "@hotwired/stimulus"
import { file_preview_status_path, file_preview_path } from "../routes"

// Connects to data-controller="file-preview"
export default class extends Controller {
  static targets = ["container"]
  static values = {
    url: String,
    fileId: Number
  }

  connect() {
    // Reset flags
    this.viewRefreshedForPreview = false
    
    // Only start polling if the element is in processing state
    // Check if this is a processing container (has spinner)
    const isProcessing = this.element.classList.contains("processing") || 
                        this.element.querySelector(".spinner-border") !== null ||
                        this.element.querySelector(".processing") !== null;
    
    if (isProcessing) {
      // Ensure processing class is set
      if (this.hasContainerTarget && !this.containerTarget.classList.contains("processing")) {
        this.containerTarget.classList.add("processing");
      }
      
      // Check the initial status only for processing files
      this.checkPreviewStatus(true);
    }
    // If not processing, do nothing - the preview is already ready
  }

  startPolling() {
    this.stopPolling()
    
    // Initialize attempt counter if not exists
    if (!this.pollingAttempts) {
      this.pollingAttempts = 0
    }
    
    this.statusTimer = setInterval(() => {
      this.pollingAttempts++
      this.checkPreviewStatus()
      
      // Stop polling after 60 seconds (30 attempts x 2 seconds)
      if (this.pollingAttempts > 30) {
        this.stopPolling()
      }
    }, 2000)
  }
  
  stopPolling() {
    if (this.statusTimer) {
      clearInterval(this.statusTimer)
      this.statusTimer = null
    }
    // Reset attempt counter
    this.pollingAttempts = 0
  }

  async checkPreviewStatus(isInitialCheck = false) {
    // Don't check status if controller is being destroyed
    if (this.isDisconnecting) {
      return
    }
    
    try {
      const statusResponse = await fetch(file_preview_status_path(this.fileIdValue))
      
      if (!statusResponse.ok) {
        return
      }
      
      const statusData = await statusResponse.json()
      
      // Check if preview is ready
      const previewReady = statusData.status === 'previewable' || statusData.preview_state === 'success'
      
      // Check if this is a Word document that needs PDF conversion
      const isWordDoc = statusData.is_word_document
      
      // Check if PDF conversion is done (converted_pdf attachment exists)
      const hasPdf = statusData.has_converted_pdf
      
      // Stop polling if:
      // 1. File is not previewable or failed
      // 2. Preview is ready AND (not a Word doc OR Word doc with PDF ready)
      const shouldStopPolling = (
        statusData.status === 'not_previewable' || 
        statusData.preview_state === 'failed' || 
        statusData.preview_state === 'protected' || 
        statusData.preview_state === 'error' ||
        (previewReady && (!isWordDoc || hasPdf))
      )
      
      if (shouldStopPolling) {
        // CRITICAL: Stop polling FIRST before refreshing view
        this.stopPolling()
        
        // Remove processing class if it exists
        if (this.hasContainerTarget && this.containerTarget.classList.contains("processing")) {
          this.containerTarget.classList.remove("processing")
        }
        
        // Wait a bit to ensure polling is fully stopped, then refresh view
        setTimeout(() => {
          this.forceRefreshView()
        }, 100)
        
        return
      }
      
      // If preview is ready but PDF is still processing, refresh view once and continue polling
      if (previewReady && isWordDoc && !hasPdf) {
        // Remove processing class and show preview
        if (this.hasContainerTarget && this.containerTarget.classList.contains("processing")) {
          this.containerTarget.classList.remove("processing")
        }
        
        // Refresh view once to show preview (will show download link until PDF is ready)
        if (!this.viewRefreshedForPreview) {
          this.viewRefreshedForPreview = true
          this.forceRefreshView()
        }
        
        // Continue polling for PDF conversion
        return
      }
      
      // For files still processing - only start polling if it's the initial check
      if (statusData.status === 'processing' || statusData.preview_state === 'processing' || 
          statusData.preview_state === 'pending') {
        
        if (this.hasContainerTarget && !this.containerTarget.classList.contains("processing")) {
          this.containerTarget.classList.add("processing")
          this.forceRefreshView()
        }
        
        // Start polling only if it's the initial check and we're not polling already
        if (isInitialCheck && !this.statusTimer) {
          this.startPolling()
        }
        return
      }
      
      // If status is unknown or unexpected, stop polling to avoid infinite loops
      this.stopPolling()
      
    } catch (error) {
      this.stopPolling()
    }
  }
  
  async forceRefreshView() {
    try {
      // Get group ID from container data attributes
      const container = this.element.closest('[data-file-group-file-id-value], [data-file-preview-target="container"]');
      let fileGroupId = 0;
      
      // Try to get group ID from various sources
      if (container) {
        // From file-group controller
        const fileGroupController = this.application.getControllerForElementAndIdentifier(container.closest('[data-controller*="file-group"]'), 'file-group');
        if (fileGroupController && fileGroupController.element.dataset.fileGroupFileIdValue) {
          // Extract group ID from the filelist container
          const fileListContainer = this.element.closest('[id^="file-group-"]');
          if (fileListContainer) {
            const match = fileListContainer.id.match(/file-group-(\d+)/);
            if (match) {
              fileGroupId = match[1];
            }
          }
        }
        
        // Fallback: check parent elements for group information
        if (!fileGroupId || fileGroupId === '0') {
          const parentWithGroup = this.element.closest('[id*="files-content-"], [id*="file-group-"]');
          if (parentWithGroup) {
            const match = parentWithGroup.id.match(/(?:files-content-|file-group-)(\d+)/);
            if (match) {
              fileGroupId = match[1];
            }
          }
        }
      }
      
      const url = new URL(file_preview_path(this.fileIdValue), window.location.origin);
      
      // Add file group ID parameter
      url.searchParams.append('file_group_id', fileGroupId);
      
      const response = await fetch(url, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })
      
      if (response.ok) {
        const html = await response.text();
        this.updatePreviewContent(html);
      }
    } catch (error) {
      // Silent fail
    }
  }
  
  updatePreviewContent(html) {
    // Use Turbo API for clean DOM updates
    window.Turbo.renderStreamMessage(html)
  }
  
  disconnect() {
    this.isDisconnecting = true
    this.stopPolling()
  }
}
